 
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { aiParseWorkReport, createWorkEntries } from '../services/api';
import { toast } from 'sonner';
import { Mic, MicOff, Sparkles, Send, CheckCircle2, PencilLine, Trash2 } from 'lucide-react';

const todayIso = () => new Date().toISOString().split('T')[0];

const newSessionId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(16).slice(2)}`);

const ParsedEntryCard = ({ entry, index, onChange, onRemove }) => (
  <div className="rounded-xl border border-[#1F3D2B]/15 bg-white p-3 space-y-2" data-testid={`parsed-entry-${index}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="font-semibold text-[#1F3D2B] truncate" data-testid={`parsed-entry-task-${index}`}>{entry.task_title}</p>
        <p className="text-xs text-[#2E2E2E]/60 truncate">{entry.project_name || '-'} · {entry.work_type_label || '-'}</p>
      </div>
      <button type="button" onClick={() => onRemove(index)} className="text-[#2E2E2E]/40 hover:text-red-600" data-testid={`parsed-entry-remove-${index}`}>
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
    <div className="grid grid-cols-2 gap-2">
      <div>
        <Label className="text-xs">Datums</Label>
        <Input type="date" value={entry.date} onChange={(e) => onChange(index, 'date', e.target.value)} className="h-10" data-testid={`parsed-entry-date-${index}`} />
      </div>
      <div>
        <Label className="text-xs">Apjoms ({entry.unit || '-'})</Label>
        <Input type="number" step="0.01" inputMode="decimal" value={entry.amount} onChange={(e) => onChange(index, 'amount', e.target.value)} className="h-10" data-testid={`parsed-entry-amount-${index}`} />
      </div>
    </div>
    <label className="flex items-center gap-2 text-sm">
      <Checkbox checked={entry.completed} onCheckedChange={(v) => onChange(index, 'completed', Boolean(v))} data-testid={`parsed-entry-completed-${index}`} />
      Darbs pabeigts
    </label>
    {entry.note ? <p className="text-xs text-[#2E2E2E]/60">{entry.note}</p> : null}
  </div>
);

export const WorkReportDialog = ({ open, onOpenChange, task = null, tasks = [], onSubmitted }) => {
  const [mode, setMode] = useState('ai');
  const [text, setText] = useState('');
  const [sessionId, setSessionId] = useState(newSessionId());
  const [messages, setMessages] = useState([]);
  const [parsed, setParsed] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const [manual, setManual] = useState({ task_id: task?.id || '', date: todayIso(), amount: '', completed: false, note: '' });

  const speechSupported = useMemo(() => typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition), []);

  useEffect(() => {
    if (open) {
      setMode('ai');
      setText('');
      setMessages([]);
      setParsed(null);
      setSessionId(newSessionId());
      setManual({ task_id: task?.id || '', date: todayIso(), amount: '', completed: false, note: '' });
    } else if (recognitionRef.current) {
      recognitionRef.current.stop();
      setListening(false);
    }
  }, [open, task?.id]);

  const toggleVoice = () => {
    if (!speechSupported) {
      toast.error('Balss ievade šajā pārlūkā nav pieejama');
      return;
    }
    if (listening && recognitionRef.current) {
      recognitionRef.current.stop();
      setListening(false);
      return;
    }
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new Recognition();
    recognition.lang = 'lv-LV';
    recognition.interimResults = false;
    recognition.continuous = true;
    recognition.onresult = (event) => {
      const chunk = Array.from(event.results).slice(event.resultIndex).map((r) => r[0].transcript).join(' ');
      setText((prev) => `${prev} ${chunk}`.trim());
    };
    recognition.onerror = () => { setListening(false); toast.error('Balss ievade neizdevās'); };
    recognition.onend = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  const sendToAi = async () => {
    if (!text.trim()) {
      toast.error('Ierakstiet, ko paveicāt');
      return;
    }
    setLoading(true);
    const userText = text.trim();
    setMessages((prev) => [...prev, { role: 'user', content: userText }]);
    setText('');
    try {
      const result = await aiParseWorkReport({ text: userText, session_id: sessionId, task_id: task?.id || null });
      setParsed(result);
      const reply = result.question || result.summary || 'Sapratu. Pārbaudiet ierakstus un iesniedziet.';
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI neizdevās apstrādāt tekstu');
    } finally {
      setLoading(false);
    }
  };

  const updateParsed = (index, field, value) => {
    setParsed((prev) => ({ ...prev, entries: prev.entries.map((e, i) => (i === index ? { ...e, [field]: value } : e)) }));
  };

  const removeParsed = (index) => {
    setParsed((prev) => ({ ...prev, entries: prev.entries.filter((_, i) => i !== index) }));
  };

  const submitEntries = async (entries, source) => {
    const payload = entries
      .filter((e) => e.task_id && Number(e.amount) > 0)
      .map((e) => ({ task_id: e.task_id, date: `${e.date}T00:00:00`, amount: Number(e.amount), completed: Boolean(e.completed), note: e.note || null }));
    if (!payload.length) {
      toast.error('Nav derīgu ierakstu (apjoms jābūt > 0)');
      return;
    }
    setSubmitting(true);
    try {
      await createWorkEntries(payload, source);
      toast.success(payload.length === 1 ? 'Atskaite iesniegta' : `Iesniegti ${payload.length} ieraksti`);
      onSubmitted?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Neizdevās iesniegt');
    } finally {
      setSubmitting(false);
    }
  };

  const manualTask = tasks.find((t) => t.id === manual.task_id) || task;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="work-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1F3D2B]" data-testid="work-report-title">
            <Sparkles className="w-5 h-5" /> Iesniegt paveikto darbu
          </DialogTitle>
          <DialogDescription data-testid="work-report-description">
            {task ? <>Uzdevums: <strong>{task.title}</strong>{task.project_name ? ` · ${task.project_name}` : ''}</> : 'Pastāstiet, ko šodien paveicāt — sistēma sakārtos ierakstus.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 rounded-xl bg-[#f2f5f2] p-1" data-testid="work-report-mode">
          <button type="button" onClick={() => setMode('ai')} className={`flex-1 rounded-lg py-2 text-sm font-medium transition-colors ${mode === 'ai' ? 'bg-[#1F3D2B] text-white shadow' : 'text-[#1F3D2B]'}`} data-testid="work-report-mode-ai">
            Brīvā tekstā (AI)
          </button>
          <button type="button" onClick={() => setMode('manual')} className={`flex-1 rounded-lg py-2 text-sm font-medium transition-colors ${mode === 'manual' ? 'bg-[#1F3D2B] text-white shadow' : 'text-[#1F3D2B]'}`} data-testid="work-report-mode-manual">
            Manuāli
          </button>
        </div>

        {mode === 'ai' ? (
          <div className="space-y-4">
            {messages.length > 0 && (
              <div className="space-y-2 max-h-48 overflow-y-auto" data-testid="work-report-messages">
                {messages.map((m, i) => (
                  <div key={i} className={`text-sm rounded-2xl px-3 py-2 max-w-[90%] ${m.role === 'user' ? 'bg-[#1F3D2B] text-white ml-auto' : 'bg-[#f2f5f2] text-[#2E2E2E]'}`} data-testid={`work-report-message-${m.role}-${i}`}>
                    {m.content}
                  </div>
                ))}
              </div>
            )}

            {parsed?.entries?.length > 0 && (
              <div className="space-y-2" data-testid="work-report-parsed">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#2E2E2E]/60 flex items-center gap-1"><PencilLine className="w-3 h-3" /> Pārbaudiet un labojiet, ja vajag</p>
                {parsed.entries.map((entry, index) => (
                  <ParsedEntryCard key={`${entry.task_id}-${index}`} entry={entry} index={index} onChange={updateParsed} onRemove={removeParsed} />
                ))}
              </div>
            )}

            <div className="space-y-2">
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={3}
                placeholder={parsed?.question ? 'Atbildiet uz jautājumu...' : task ? 'Piem.: Šodien nostigoju 4,8 ha, darbs pabeigts.' : 'Piem.: Šodien Ozolos nostigoju 4,8 ha un dastoju 3,6 ha. Stigošana pabeigta.'}
                className="text-base"
                data-testid="work-report-text"
              />
              <div className="flex gap-2">
                <Button type="button" variant={listening ? 'destructive' : 'outline'} onClick={toggleVoice} className="h-12 px-4" data-testid="work-report-voice">
                  {listening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                </Button>
                <Button type="button" onClick={sendToAi} disabled={loading} className="flex-1 h-12 bg-[#1F3D2B] hover:bg-[#2a5039]" data-testid="work-report-send">
                  {loading ? 'Apstrādā...' : (<><Send className="w-4 h-4 mr-2" /> {parsed ? 'Precizēt' : 'Nosūtīt'}</>)}
                </Button>
              </div>
            </div>

            {parsed?.entries?.length > 0 && (
              <Button type="button" onClick={() => submitEntries(parsed.entries, 'ai')} disabled={submitting} className="w-full h-12 bg-green-700 hover:bg-green-800 text-base" data-testid="work-report-submit">
                <CheckCircle2 className="w-5 h-5 mr-2" /> {submitting ? 'Iesniedz...' : `Iesniegt ${parsed.entries.length} ${parsed.entries.length === 1 ? 'ierakstu' : 'ierakstus'}`}
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3" data-testid="work-report-manual">
            {!task && (
              <div className="space-y-1">
                <Label>Darba uzdevums</Label>
                <Select value={manual.task_id} onValueChange={(v) => setManual((p) => ({ ...p, task_id: v }))}>
                  <SelectTrigger className="h-12" data-testid="work-report-manual-task"><SelectValue placeholder="Izvēlieties uzdevumu" /></SelectTrigger>
                  <SelectContent>
                    {tasks.filter((t) => t.status !== 'completed').map((t) => (
                      <SelectItem key={t.id} value={t.id} data-testid={`work-report-manual-task-${t.id}`}>{t.title}{t.project_name ? ` · ${t.project_name}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Datums</Label>
                <Input type="date" value={manual.date} onChange={(e) => setManual((p) => ({ ...p, date: e.target.value }))} className="h-12" data-testid="work-report-manual-date" />
              </div>
              <div className="space-y-1">
                <Label>Apjoms ({manualTask?.unit || '-'})</Label>
                <Input type="number" step="0.01" inputMode="decimal" value={manual.amount} onChange={(e) => setManual((p) => ({ ...p, amount: e.target.value }))} className="h-12 text-lg" data-testid="work-report-manual-amount" />
              </div>
            </div>
            <label className="flex items-center gap-3 rounded-xl border p-3 text-base">
              <Checkbox checked={manual.completed} onCheckedChange={(v) => setManual((p) => ({ ...p, completed: Boolean(v) }))} data-testid="work-report-manual-completed" />
              Darbs pabeigts
            </label>
            <div className="space-y-1">
              <Label>Piezīme (neobligāti)</Label>
              <Input value={manual.note} onChange={(e) => setManual((p) => ({ ...p, note: e.target.value }))} className="h-12" data-testid="work-report-manual-note" />
            </div>
            <Button
              type="button"
              disabled={submitting}
              onClick={() => submitEntries([{ ...manual, task_id: manual.task_id || task?.id }], 'manual')}
              className="w-full h-12 bg-[#1F3D2B] hover:bg-[#2a5039] text-base"
              data-testid="work-report-manual-submit"
            >
              {submitting ? 'Iesniedz...' : 'Iesniegt'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WorkReportDialog;
